import { useState } from 'react';
import {
	DndContext,
	PointerSensor,
	TouchSensor,
	useSensor,
	useSensors,
	closestCenter,
} from '@dnd-kit/core';
import {
	SortableContext,
	arrayMove,
	useSortable,
	rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';
import { AdminLayout } from 'layout/AdminLayout';
import { useAddCampaignRewardImage, useCreateCampaignReward } from 'api/admin/hooks';
import {
	CAMPAIGN_ID,
	RESTAURANT_ID,
	DEVICE_TOKEN
} from 'utils/constants';
import Styles from './createReward.module.scss';

const SortableImage = ({
	image,
	index,
	onDelete
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition
	} = useSortable({ id: image.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const handleDelete = (e) => {
		e.stopPropagation();

		onDelete(image.id);
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={Styles.imageItem}
			{...attributes}
			{...listeners}
		>
			<img src={image.preview} alt={`uploaded-${index}`} />
			<span className={Styles.orderBadge}>{index + 1}</span>
			<button
				type="button"
				className={Styles.deleteButton}
				onClick={handleDelete}
				aria-label="Delete image"
			>
				✕
			</button>
		</div>
	);
};

export const CreateRewardScreen = () => {
	const [name, setName] = useState('');
	const [quantity, setQuantity] = useState(0);
	const [description, setDescription] = useState('');
	const [images, setImages] = useState([]);

	const {
		mutateAsync: addCampaignRewardImage,
		isPending: isUploadingImage
	} = useAddCampaignRewardImage();

	const {
		mutateAsync: createReward,
		isPending: isCreatingReward
	} = useCreateCampaignReward({
		onError: (error) => {
			const defaultMessage = 'Something went wrong while creating reward';
			const responseMessage = error?.response?.data?.message;
			const errorMessage = responseMessage && responseMessage.length <= 100
				? responseMessage
				: defaultMessage;

			toast.error(errorMessage);
		},
	});

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: 5 },
		}),
		useSensor(TouchSensor, {
			activationConstraint: { delay: 150, tolerance: 8 },
		})
	);

	const handleImageUpload = (e) => {
		if (!e.target.files) return;

		const uploaded = Array.from(e.target.files).map((file) => ({
			id: crypto.randomUUID(),
			file,
			preview: URL.createObjectURL(file),
		}));

		setImages((prev) => [...prev, ...uploaded]);
		e.target.value = '';
	};

	const handleDragEnd = (event) => {
		const { active, over } = event;

		if (!over || active.id === over.id) return;

		setImages((items) => {
			const oldIndex = items.findIndex((i) => i.id === active.id);
			const newIndex = items.findIndex((i) => i.id === over.id);

			return arrayMove(items, oldIndex, newIndex);
		});
	};

	const handleDeleteImage = (id) => {
		setImages((prev) => {
			const imgToRemove = prev.find((img) => img.id === id);

			if (imgToRemove) {
				URL.revokeObjectURL(imgToRemove.preview);
			}

			return prev.filter((img) => img.id !== id);
		});
	};

	const handleSubmit = async () => {
		try {
			const data = await createReward({
				restaurantID: RESTAURANT_ID,
				campaignID: CAMPAIGN_ID,
				employeeID: localStorage.getItem('employeeID'),
				deviceToken: DEVICE_TOKEN,
				name,
				quantity,
				description
			});

			const campaignRewardID = data.campaign_reward.id;

			await Promise.all(
				images.map((image, index) => {
					const formData = new FormData();
					formData.append('campaign_reward_image', image.file);
					formData.append('image_order', index + 1);

					return addCampaignRewardImage({
						restaurantID: RESTAURANT_ID,
						campaignID: CAMPAIGN_ID,
						employeeID: localStorage.getItem('employeeID'),
						deviceToken: DEVICE_TOKEN,
						rewardID: campaignRewardID,
						formData,
					});
				})
			);

			setName('');
			setDescription('');
			setQuantity(0);
			setImages([]);

			toast.success('Campaign Reward Created');
		} catch (error) {
			const responseMessage = error?.response?.data?.message;

			toast.error(
				responseMessage && responseMessage.length <= 100
					? responseMessage
					: 'Something went wrong while uploading images'
			);
		}
	};

	return (
		<AdminLayout>
			<div className={Styles.container}>
				<form onSubmit={() => { }} className={Styles.form}>
					<div className='d-flex flex-column gap-2 w-100'>
						<label>Name</label>
						<input
							className={Styles.input}
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
						<label>Quantity</label>
						<input
							className={Styles.input}
							value={quantity}
							type='number'
							onChange={(e) => setQuantity(e.target.value)}
						/>
						<label>Description</label>
						<textarea
							className={Styles.input}
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
						<label>Images</label>
						<input
							type="file"
							accept="image/*"
							multiple
							onChange={handleImageUpload}
						/>
					</div>
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={images.map((i) => i.id)}
							strategy={rectSortingStrategy}
						>
							<div className={Styles.imageGrid}>
								{images.map((image, index) => (
									<SortableImage
										key={image.id}
										image={image}
										index={index}
										onDelete={handleDeleteImage}
									/>
								))}
							</div>
						</SortableContext>
					</DndContext>
					{images.length > 0 && (
						<button
							className={Styles.submitButton}
							type="button"
							disabled={
								!name
								|| !images.length > 0
								|| isCreatingReward
								|| isUploadingImage
							}
							onClick={handleSubmit}
						>
							Submit
						</button>
					)}
				</form>

			</div>
		</AdminLayout>
	);
};
